import dataProvider, { apiUrl } from "./dataProvider";

const authProvider = {
  login: ({ email, password }: any) => {
    const type = "superadmin";
    const request = new Request(`${apiUrl}/auth/login`, {
      method: "POST",
      body: JSON.stringify({ email, password, type }),
      headers: new Headers({ "Content-Type": "application/json" }),
    });

    return fetch(request)
      .then((response) => {
        if (response.status < 200 || response.status >= 300) {
          throw new Error(response.statusText);
        }
        return response.json();
      })
      .then(({ token }) => {
        // store  jwt to localstorage
        // then read it's payload
        const payload = parseJwt(token);

        // check if parent, will reject the login
        const rolesPayload: string[] =
          payload[
            "http://schemas.microsoft.com/ws/2008/06/identity/claims/role"
          ];

        if (
          rolesPayload.includes("parent") ||
          rolesPayload.includes("company") ||
          rolesPayload.includes("personInCharge")
        ) {
          clearLocalStorage();
          return Promise.reject();
        }

        localStorage.setItem("token", token);
        localStorage.setItem("roles", rolesPayload.toString());
        localStorage.setItem("userId", payload["userId"]);

        return payload;
      })
      .then(({ user }) => {
        // set the properties of the app,
        // based on the payload.
        localStorage.setItem("me", user);
        return Promise.resolve();
      });
  },
  logout: () => {
    clearLocalStorage();
    return Promise.resolve();
  },
  checkAuth: () => {
    return localStorage.getItem("token") ? Promise.resolve() : Promise.reject();
  },
  checkError: (error: any) => {
    const status = error.status;
    if (status === 401) {
      localStorage.removeItem("token");

      return Promise.reject({ redirectTo: "/login?err=1" });
    }
    return Promise.resolve();
  },

  getPermissions: () => {
    const availableResourcesForAdmin = [
      "schools",
      "students",
      "parents",
      "studentRenewals",
      "ccaStudentRenewals",
      "changeRequestForms",
      "parentsWithoutStudent",
      "assignments",
      "busSchedulePlans",
      "drivers",
      "busAssistants",
      "vehicles",
      "liveBusLocations",
      "notTakings",
      "notTakingStudents",
      "unapprovedInvoices",
      "invoices",
      "unapprovedCreditNotes",
      "creditNotes",
      "customerReceipts",
      "customerCards",
      "companies",
      "personInCharges",
      "subcontractors",
      "subcontractorTimesheets",
      "subcontractorAdhocFees",
      "subcontractorRouteTransactions",
      "subcontractorPurchaseInvoices",
      "notifications",
      "emailSendingLogs",
      "totalNoOfAssignments",
      "calculateCostFeatures",
      "findCommercialDocumentFeatures",
      "subcontractorErps",
      "subcontractorAdjustmentPrices",
      "excludeBusAssistants",
      "adhocOperators",
    ];

    const availableResourcesForSuperAdmin = [
      ...availableResourcesForAdmin,
      "users",
      "dryRunSchedules",
      "exportDataFeatures",
    ];

    const availableResourcesForCustomerService = [
      "students",
      "parents",
      "studentRenewals",
      "ccaStudentRenewals",
      "changeRequestForms",
      "parentsWithoutStudent",
      "companies",
      "personInCharges",
      "calculateCostFeatures",
    ];

    const availableResourcesForFinance = [
      "students",
      "parents",
      "invoices",
      "creditNotes",
      "customerReceipts",
      "customerCards",
      "companies",
      "personInCharges",
      "findCommercialDocumentFeatures",
    ];

    const availableResourcesForOperation = [
      "changeRequestForms",
      "assignments",
      "busSchedulePlans",
      "drivers",
      "busAssistants",
      "vehicles",
      "liveBusLocations",
      "notTakings",
      "notTakingStudents",
      "subcontractors",
      "subcontractorTimesheets",
      "notifications",
      "totalNoOfAssignments",
      "excludeBusAssistants",
      "adhocOperators",
    ];

    const availableResourcesForSchool = [
      "students",
      "parents",
      "assignments",
    ];

    const roles: string[] = localStorage.getItem("roles")
      ? localStorage.getItem("roles").split(",")
      : [];

    let availablePermissions: string[] = [];

    if (roles.includes("superadmin")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForSuperAdmin,
      ];
    }

    if (roles.includes("admin")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForAdmin,
      ];
    }

    if (roles.includes("customerservice")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForCustomerService,
      ];
    }

    if (roles.includes("finance")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForFinance,
      ];
    }

    if (roles.includes("operation")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForOperation,
      ];
    }

    if (roles.includes("school")) {
      availablePermissions = [
        ...availablePermissions,
        ...availableResourcesForSchool,
      ];
    }

    return Promise.resolve(availablePermissions);
  },
  getIdentity: async () => {
    const userId: string = localStorage.getItem("userId");
    const email: string = localStorage.getItem("email");

    if (email === null) {
      // fetch roles and permissions endpoint
      return dataProvider
        .getOne("users", { id: userId })
        .then(({ data }) => {
          const { id, email } = data;

          localStorage.setItem("email", email);

          return {
            id: id,
            fullName: `${email}`,
          };
        })
        .catch(() => {
          return Promise.reject();
        });
    }

    return Promise.resolve({
      id: userId,
      fullName: `${email}`,
    });
  },
};

const clearLocalStorage = () => {
  localStorage.removeItem("token");
  localStorage.removeItem("me");
  localStorage.removeItem("roles");
  localStorage.removeItem("permissions");
  localStorage.removeItem("loginType");
  localStorage.removeItem("tenantId");
  localStorage.removeItem("userId");
  localStorage.removeItem("email");
  sessionStorage.setItem("school", null);
};

/**
 * A function to parse JWT to get the paylod
 *
 * @param token JWT string
 */
const parseJwt = (token: string) => {
  var base64Url = token.split(".")[1];
  var base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
  var jsonPayload = decodeURIComponent(
    atob(base64)
      .split("")
      .map(function (c) {
        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
      })
      .join(""),
  );

  return JSON.parse(jsonPayload);
};

export default authProvider;
